import { Autocomplete } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo, useCallback, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __ANPR_PERSON_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { Operation } from 'shell/view-models/operation';

export const CODE_APOLIDE = '198';
export const ID_NON_ATTRIBUIBILE = '998';
export const ID_ALTRO_STATO = '200';

export const LABEL_ALTRO_STATO = 'ALTRO STATO';

interface ForeignStateListComponentProps {
  disabled?: boolean;
  value: ForeignStateVM | null;
  onChange: (foreignState?: ForeignStateVM | null) => void;
  error?: boolean;
  required?: boolean;
  label?: string;
  id?: string;
}

type Key = {
  codice: number;
};

interface ForeignState {
  key: Key;
  codicePostazioneAnpr: string;
  dataIstituzione: Date;
  dataCessazione: Date;
  codiceIstat: string;
  codiceCatastale: string;
  denominazione: string;
  denominazioneTraslitterata: string;
  altraDenominazione: string;
  altraDenominazioneTraslitterata: string;
  idProvincia: number;
  idProvinciaIstat: number;
  idRegione: string;
  idPrefettura: string;
  codiceStato: string;
  codiceSiglaProvincia: string;
  fonte: string;
  dataUltimoAggiornamento: Date;
  codiceDenominazione: string;
  codiceIso?: string;
  codiceMae?: string;
}

export interface ForeignStateVM {
  code?: string;
  id: string;
  value: string;
  istatCode?: string;
  province?: string;
  nationCode?: string;
  codiceMae?: string;
}

const ForeignStateListComponent = ({
  disabled,
  onChange,
  required,
  value: selectedValue,
  error,
  label = 'Stato Estero',
  id = 'foreignState',
}: ForeignStateListComponentProps): React.ReactElement => {
  const [foreignState, getForeignState] = useHttpFetch<
    HttpResponseList<ForeignState>,
    Array<ForeignStateVM>
  >(`${__ANPR_PERSON_API_URL__}/stato-estero`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<ForeignState>>) => {
      const { data } = response;

      return data?.payload.list.map((item) => ({
        id: item.codiceIstat,
        value: item.denominazione,
        province: item.codiceIso,
        istatCode: item.codiceIstat,
        nationCode: item?.codiceStato,
        code: item.key.codice?.toString(),
        codiceMae: item.codiceMae,
      })) as Array<ForeignStateVM>;
    },
  });

  const defaultProps = useMemo(
    () => ({
      options: (foreignState.data as Array<ForeignStateVM>) || [],
      getOptionLabel: (option: ForeignStateVM | null) => option?.value || '',
    }),
    [foreignState]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = foreignState;

        abort();

        getForeignState({
          params: {},
          paramsSerializer: () => `filters=denominazione:${value}:${Operation.LIKE}`,
        });
      },
      [foreignState.abort, getForeignState]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      onKeyPress={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 2 && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={foreignState.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

ForeignStateListComponent.displayName = 'Foreign state list component';

const ForeignStateList = memo(ForeignStateListComponent);

export default ForeignStateList as typeof ForeignStateListComponent;
